import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  Plus,
  Shield,
  Eye,
  CheckCircle2,
  XCircle,
  Clock,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  X,
  Search,
  FileSearch,
  Lock,
  Fingerprint,
  Database,
  Scale,
  FileText,
  UserCheck,
} from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type ReviewType = 'new_system' | 'new_process' | 'change_request' | 'vendor_assessment' | 'periodic' | 'incident_driven';
type ReviewStatus = 'pending' | 'in_review' | 'approved' | 'rejected' | 'requires_dpia';
type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
type Recommendation = 'approve' | 'reject' | 'approve_with_conditions' | 'requires_dpia';

interface ChecklistItem {
  key: string;
  label: string;
  checked: boolean;
  notes: string | null;
}

interface PrivacyReview {
  id: number;
  title: string;
  description: string | null;
  review_type: ReviewType;
  status: ReviewStatus;
  overall_risk: RiskLevel | null;
  requested_by_name: string | null;
  reviewer_name: string | null;
  review_date: string | null;
  recommendation: Recommendation | null;
  conditions: string | null;
  checklist: ChecklistItem[] | null;
  created_at: string;
}

interface DashboardStats {
  pending: number;
  in_review: number;
  approved: number;
  rejected: number;
}

interface CreateForm {
  title: string;
  description: string;
  review_type: ReviewType;
}

interface DecisionForm {
  recommendation: Recommendation;
  conditions: string;
  overall_risk: RiskLevel;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const REVIEW_TYPES: ReviewType[] = ['new_system', 'new_process', 'change_request', 'vendor_assessment', 'periodic', 'incident_driven'];
const STATUSES: ReviewStatus[] = ['pending', 'in_review', 'approved', 'rejected', 'requires_dpia'];
const RISK_LEVELS: RiskLevel[] = ['low', 'medium', 'high', 'critical'];
const RECOMMENDATIONS: Recommendation[] = ['approve', 'reject', 'approve_with_conditions', 'requires_dpia'];

const STATUS_BADGE: Record<ReviewStatus, string> = {
  pending: 'bg-blue-100 text-blue-800',
  in_review: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  requires_dpia: 'bg-purple-100 text-purple-800',
};

const RISK_BADGE: Record<RiskLevel, string> = {
  low: 'bg-green-100 text-green-700',
  medium: 'bg-yellow-100 text-yellow-700',
  high: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

const TYPE_ICON: Record<ReviewType, React.ElementType> = {
  new_system: Database,
  new_process: FileSearch,
  change_request: RefreshCw,
  vendor_assessment: UserCheck,
  periodic: Clock,
  incident_driven: AlertTriangle,
};

const CHECKLIST_LABELS: Record<string, { label: string; icon: React.ElementType }> = {
  data_minimisation: { label: 'Data Minimisation', icon: Database },
  purpose_limitation: { label: 'Purpose Limitation', icon: Scale },
  storage_limitation: { label: 'Storage Limitation', icon: Lock },
  security_measures: { label: 'Security Measures', icon: Shield },
  lawful_basis: { label: 'Lawful Basis', icon: FileText },
  transparency: { label: 'Transparency', icon: Eye },
  rights_impact: { label: 'Rights Impact', icon: Fingerprint },
};

const fmtLabel = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '--';

/* ------------------------------------------------------------------ */
/*  API helpers                                                        */
/* ------------------------------------------------------------------ */

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

const API = '/api/compliance/privacy-reviews';

async function apiFetch<T>(url: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(url, { headers: authHeaders(), ...opts });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Request failed (${res.status})`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const PrivacyReviews: React.FC = () => {
  const [reviews, setReviews] = useState<PrivacyReview[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Expanded row
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<CreateForm>({ title: '', description: '', review_type: 'new_system' });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Decision modal
  const [decisionReview, setDecisionReview] = useState<PrivacyReview | null>(null);
  const [decisionForm, setDecisionForm] = useState<DecisionForm>({ recommendation: 'approve', conditions: '', overall_risk: 'low' });
  const [decidingError, setDecidingError] = useState('');

  /* ---------- Load ---------- */

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (typeFilter) params.set('review_type', typeFilter);
      if (searchTerm) params.set('search', searchTerm);
      params.set('limit', '100');
      const qs = params.toString();

      const [listRes, statsRes] = await Promise.all([
        apiFetch<any>(`${API}${qs ? `?${qs}` : ''}`),
        apiFetch<DashboardStats>(`${API}/dashboard`),
      ]);

      setReviews(Array.isArray(listRes) ? listRes : listRes.reviews ?? listRes.data ?? []);
      setStats(statsRes);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, typeFilter, searchTerm]);

  useEffect(() => { load(); }, [load]);

  /* ---------- Create ---------- */

  const handleCreate = async () => {
    if (!createForm.title.trim()) { setFormError('Title is required.'); return; }
    setSubmitting(true);
    setFormError('');
    try {
      await apiFetch(API, { method: 'POST', body: JSON.stringify(createForm) });
      setShowCreate(false);
      setCreateForm({ title: '', description: '', review_type: 'new_system' });
      load();
    } catch (e: any) {
      setFormError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- Decision ---------- */

  const openDecision = (review: PrivacyReview) => {
    setDecisionReview(review);
    setDecisionForm({ recommendation: 'approve', conditions: '', overall_risk: 'medium' });
    setDecidingError('');
  };

  const handleDecision = async () => {
    if (!decisionReview) return;
    setSubmitting(true);
    setDecidingError('');
    try {
      await apiFetch(`${API}/${decisionReview.id}/decision`, {
        method: 'POST',
        body: JSON.stringify(decisionForm),
      });
      setDecisionReview(null);
      load();
    } catch (e: any) {
      setDecidingError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- Render ---------- */

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-xl">
            <Shield size={24} className="text-purple-600" />
          </div>
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Privacy Reviews</h1><PageInfoButton /></div>
            <p className="text-sm text-slate-500">Privacy-by-Design review gates for systems, processes, and changes</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition">
            <RefreshCw size={16} /> Refresh
          </button>
          <button onClick={() => { setShowCreate(true); setFormError(''); }} className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition shadow-sm">
            <Plus size={16} /> New Review
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex items-center gap-2">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Pending', value: stats.pending, icon: Clock, color: 'text-blue-700', bg: 'bg-blue-50' },
            { label: 'In Review', value: stats.in_review, icon: Eye, color: 'text-amber-700', bg: 'bg-amber-50' },
            { label: 'Approved', value: stats.approved, icon: CheckCircle2, color: 'text-green-700', bg: 'bg-green-50' },
            { label: 'Rejected', value: stats.rejected, icon: XCircle, color: 'text-red-700', bg: 'bg-red-50' },
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className={`${bg} border border-slate-200 rounded-xl p-4`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon size={16} className={color} />
                <p className="text-sm text-slate-500">{label}</p>
              </div>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 rounded-xl p-4">
        <Search size={16} className="text-slate-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search reviews..."
          className="border border-slate-300 rounded-lg px-3 py-2 text-sm flex-1 min-w-[180px] focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{fmtLabel(s)}</option>)}
        </select>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
          <option value="">All Types</option>
          {REVIEW_TYPES.map(t => <option key={t} value={t}>{fmtLabel(t)}</option>)}
        </select>
        {(statusFilter || typeFilter || searchTerm) && (
          <button onClick={() => { setStatusFilter(''); setTypeFilter(''); setSearchTerm(''); }} className="text-xs text-slate-500 hover:text-slate-700 underline">
            Clear filters
          </button>
        )}
      </div>

      {/* Loading */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
        </div>
      ) : reviews.length === 0 ? (
        <div className="py-16 bg-white border border-slate-200 rounded-xl">
          <Shield size={48} className="mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500 text-lg font-medium">No privacy reviews found</p>
          <p className="text-slate-400 text-sm mt-1">Create a new privacy review request to get started.</p>
          <button onClick={() => setShowCreate(true)} className="mt-4 inline-flex items-center gap-1 px-4 py-2 text-sm text-white bg-purple-600 rounded-lg hover:bg-purple-700">
            <Plus size={16} /> New Review
          </button>
        </div>
      ) : (
        /* Table */
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                {['Title', 'Review Type', 'Requested By', 'Status', 'Risk', 'Review Date', ''].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {reviews.map(review => {
                const isExpanded = expandedId === review.id;
                const TypeIcon = TYPE_ICON[review.review_type] || FileSearch;
                return (
                  <React.Fragment key={review.id}>
                    <tr className="hover:bg-slate-50 cursor-pointer transition" onClick={() => setExpandedId(isExpanded ? null : review.id)}>
                      <td className="px-4 py-3 text-left font-medium text-slate-900">{review.title}</td>
                      <td className="px-4 py-3 text-left">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                          <TypeIcon size={12} /> {fmtLabel(review.review_type)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-left text-slate-600">{review.requested_by_name || '--'}</td>
                      <td className="px-4 py-3 text-left">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_BADGE[review.status]}`}>
                          {fmtLabel(review.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-left">
                        {review.overall_risk ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${RISK_BADGE[review.overall_risk]}`}>
                            {fmtLabel(review.overall_risk)}
                          </span>
                        ) : (
                          <span className="text-slate-400 text-xs">--</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-left text-slate-600 text-xs">{fmtDate(review.review_date)}</td>
                      <td className="px-4 py-3 text-left text-slate-400">
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </td>
                    </tr>

                    {/* Expanded checklist */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={7} className="bg-slate-50 px-6 py-5 border-t border-slate-100">
                          <div className="space-y-4">
                            {/* Description */}
                            {review.description && (
                              <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Description</p>
                                <p className="text-sm text-slate-700">{review.description}</p>
                              </div>
                            )}

                            {/* Conditions */}
                            {review.conditions && (
                              <div>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Conditions</p>
                                <p className="text-sm text-slate-700">{review.conditions}</p>
                              </div>
                            )}

                            {/* Privacy-by-Design Checklist */}
                            <div>
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-2">Privacy-by-Design Checklist</p>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                {(review.checklist && review.checklist.length > 0
                                  ? review.checklist
                                  : Object.entries(CHECKLIST_LABELS).map(([key, { label }]) => ({ key, label, checked: false, notes: null }))
                                ).map(item => {
                                  const meta = CHECKLIST_LABELS[item.key];
                                  const Icon = meta?.icon || Shield;
                                  return (
                                    <div key={item.key} className="flex items-start gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2.5">
                                      <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${item.checked ? 'bg-green-100 text-green-600' : 'bg-slate-100 text-slate-400'}`}>
                                        {item.checked ? <CheckCircle2 size={14} /> : <Icon size={12} />}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-medium ${item.checked ? 'text-slate-900' : 'text-slate-500'}`}>
                                          {meta?.label || fmtLabel(item.key)}
                                        </p>
                                        {item.notes && <p className="text-xs text-slate-400 mt-0.5 truncate">{item.notes}</p>}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Actions */}
                            {(review.status === 'pending' || review.status === 'in_review') && (
                              <div className="flex justify-end">
                                <button
                                  onClick={e => { e.stopPropagation(); openDecision(review); }}
                                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition shadow-sm"
                                >
                                  Submit Decision
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ---- Create Modal ---- */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 className="text-lg font-semibold text-slate-900">New Privacy Review Request</h2>
              <button onClick={() => setShowCreate(false)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {formError && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{formError}</div>}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={createForm.title}
                  onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. New CRM System Privacy Review"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  value={createForm.description}
                  onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                  rows={3}
                  placeholder="Describe the system, process, or change requiring privacy review..."
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Review Type</label>
                <select
                  value={createForm.review_type}
                  onChange={e => setCreateForm(f => ({ ...f, review_type: e.target.value as ReviewType }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                >
                  {REVIEW_TYPES.map(t => <option key={t} value={t}>{fmtLabel(t)}</option>)}
                </select>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={handleCreate} disabled={submitting} className="px-5 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 shadow-sm">
                {submitting ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Decision Modal ---- */}
      {decisionReview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Review Decision</h2>
                <p className="text-sm text-slate-500 mt-0.5">{decisionReview.title}</p>
              </div>
              <button onClick={() => setDecisionReview(null)} className="text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {decidingError && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{decidingError}</div>}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Recommendation *</label>
                <select
                  value={decisionForm.recommendation}
                  onChange={e => setDecisionForm(f => ({ ...f, recommendation: e.target.value as Recommendation }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                >
                  {RECOMMENDATIONS.map(r => <option key={r} value={r}>{fmtLabel(r)}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Overall Risk Assessment</label>
                <select
                  value={decisionForm.overall_risk}
                  onChange={e => setDecisionForm(f => ({ ...f, overall_risk: e.target.value as RiskLevel }))}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                >
                  {RISK_LEVELS.map(r => <option key={r} value={r}>{fmtLabel(r)}</option>)}
                </select>
              </div>
              {(decisionForm.recommendation === 'approve_with_conditions' || decisionForm.recommendation === 'requires_dpia') && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {decisionForm.recommendation === 'requires_dpia' ? 'DPIA Requirements' : 'Conditions'}
                  </label>
                  <textarea
                    value={decisionForm.conditions}
                    onChange={e => setDecisionForm(f => ({ ...f, conditions: e.target.value }))}
                    rows={3}
                    placeholder={decisionForm.recommendation === 'requires_dpia' ? 'Describe what the DPIA should cover...' : 'Specify conditions for approval...'}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none resize-none"
                  />
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-xl">
              <button onClick={() => setDecisionReview(null)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
              <button onClick={handleDecision} disabled={submitting} className="px-5 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50 shadow-sm">
                {submitting ? 'Submitting...' : 'Submit Decision'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrivacyReviews;
